import React, { useCallback, useEffect, useState } from 'react';
import { Upload, Link as LinkIcon, FileText, Trash2, Plus, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { knowledgeService, type KnowledgeItem } from '@/services/knowledgeService';
import PageLayout from '@/components/layout/PageLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { projectService, type ProjectDto } from '@/services/projectService';

const MemorySourcesPage: React.FC = () => {
  const [sources, setSources] = useState<KnowledgeItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isUrlOpen, setIsUrlOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [refreshingIds, setRefreshingIds] = useState<Record<string, boolean>>({});
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('TEAM');

  const isValidUrl = (url: string): boolean => {
    try {
      const parsedUrl = new URL(url);
      return ['http:', 'https:'].includes(parsedUrl.protocol);
    } catch {
      return false;
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      setIsUploading(true);
      try {
        for (const file of Array.from(files)) {
          if (file.type === 'application/pdf') {
            try {
              await knowledgeService.ingestPdf(file, selectedProjectId);
              await loadKnowledge();
              toast.success(`${file.name} uploaded successfully`);
            } catch (e) {
              toast.error('Failed to upload PDF');
            }
          } else {
            toast.error('Only PDF files are allowed');
          }
        }
      } finally {
        setIsUploading(false);
      }
    }
    setIsUploadOpen(false);
  };

  const loadKnowledge = useCallback(async () => {
    try {
      setIsLoading(true);
      const items = await knowledgeService.getAll(selectedProjectId);
      setSources(items);
    } catch (e) {
      // silent fail
    } finally {
      setIsLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    loadKnowledge();
  }, [loadKnowledge]);

  useEffect(() => {
    (async () => {
      try {
        const list = await projectService.list();
        setProjects(list);
      } catch {
        // ignore
      }
    })();
  }, []);

  const handleUrlAdd = async () => {
    const trimmedUrl = urlInput.trim();
    if (!trimmedUrl) {
      toast.error('Please enter a URL');
      return;
    }
    
    if (!isValidUrl(trimmedUrl)) {
      toast.error('Please enter a valid URL (must include http:// or https://)');
      return;
    }

    try {
      setIsUploading(true);
      await knowledgeService.ingestUrl(trimmedUrl, selectedProjectId);
      await loadKnowledge();
      setUrlInput('');
      setIsUrlOpen(false);
      toast.success('URL added successfully');
    } catch {
      toast.error('Failed to add URL');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await knowledgeService.delete(id);
      await loadKnowledge();
      toast.success('Source removed successfully');
    } catch (e) {
      toast.error('Failed to delete source');
    }
  };

  const handleRefreshUrl = async (source: KnowledgeItem) => {
    if (!source.url) return;
    setRefreshingIds(prev => ({ ...prev, [source.id]: true }));
    try {
      // delete existing
      await knowledgeService.delete(source.id);
      // re-ingest same URL
      await knowledgeService.ingestUrl(source.url, selectedProjectId);
      await loadKnowledge();
      toast.success('URL refreshed');
    } catch (e) {
      toast.error('Failed to refresh URL');
    } finally {
      setRefreshingIds(prev => ({ ...prev, [source.id]: false }));
    }
  };

  return (
    <PageLayout
      title="Knowledge Sources"
      description="Upload documents and add URLs to train your AI agent with the latest information"
      
    >
      <div className="space-y-8">

        {/* Action Buttons */}
		{projects.length > 0 && (
			<div className="flex items-center gap-3 mb-4">
				<div className="min-w-[260px]">
					<Select value={selectedProjectId} onValueChange={(val) => setSelectedProjectId(val)}>
						<SelectTrigger>
							<SelectValue placeholder="Team" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="TEAM">Team</SelectItem>
							{projects.map((p) => (
								<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>
		)}
		<div className="flex gap-4">
        <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-2 border-dashed border-primary/20 hover:border-primary/40 hover:bg-primary/5 transition-colors duration-200">
              <Upload className="w-4 h-4 mr-2" />
              Upload PDF
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload PDF Document</DialogTitle>
              <DialogDescription>
                Select PDF files to add to your knowledge base
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="file-upload">Choose PDF files</Label>
                <Input
                  id="file-upload"
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={handleFileUpload}
                  disabled={isUploading}
                  className="mt-2 h-12 file:mr-4 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:px-4 file:py-2 file:cursor-pointer hover:file:bg-primary/90 disabled:opacity-60"
                />
              </div>
              {isUploading && (
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                  Uploading document(s)... This may take a moment.
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={isUrlOpen} onOpenChange={setIsUrlOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-2 border-dashed border-primary/20 hover:border-primary/40 hover:bg-primary/5 transition-colors duration-200">
              <Plus className="w-4 h-4 mr-2" />
              Add URL
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add URL Resource</DialogTitle>
              <DialogDescription>
                Add a web page or documentation URL to your knowledge base
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="url-input">URL</Label>
                <Input
                  id="url-input"
                  type="url"
                  placeholder="https://example.com/docs"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  className="mt-2"
                  disabled={isUploading}
                />
              </div>
                {isUploading && (
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                    Processing URL... This may take a moment.
                  </div>
                )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsUrlOpen(false)} disabled={isUploading}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleUrlAdd}
                  disabled={isUploading || !urlInput.trim() || !isValidUrl(urlInput.trim())}
                >
                  Add URL
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        </div>

        {/* Sources Grid */}
        {isLoading ? (
          <Card className="border border-muted-foreground/10">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin mb-4" />
              <h3 className="text-sm text-muted-foreground">Loading sources...</h3>
            </CardContent>
          </Card>
        ) : sources.length === 0 ? (
          <Card className="border border-muted-foreground/10">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="w-12 h-12 text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-semibold text-muted-foreground mb-2">No sources yet</h3>
              <p className="text-muted-foreground/80 mb-4">Upload PDFs or add URLs using the buttons above to get started</p>
            </CardContent>
          </Card>
        ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sources.map((source) => (
            <Card key={source.id} className="hover:shadow-md transition-shadow duration-200 group">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    {source.type === 'pdf' ? (
                      <div className="p-2 rounded-lg bg-destructive/10">
                        <FileText className="w-5 h-5 text-destructive" />
                      </div>
                    ) : (
                      <div className="p-2 rounded-lg bg-primary/10">
                        <LinkIcon className="w-5 h-5 text-primary" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <CardTitle className="text-base font-semibold truncate w-full" title={source.title}>
                        {source.title}
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">
                          {source.type.toUpperCase()}
                        </Badge>
                        {source.size && (
                          <span className="text-xs text-muted-foreground">{source.size}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {source.type === 'url' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRefreshUrl(source)}
                        disabled={!!refreshingIds[source.id]}
                        className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-muted-foreground"
                        title="Refresh URL"
                      >
                        {refreshingIds[source.id] ? (
                          <div className="w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(source.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {source.description && (
                  <CardDescription className="mb-3">
                    {source.description}
                  </CardDescription>
                )}
                {source.url && (
                  <div className="mb-3">
                    <a 
                      href={source.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline truncate block"
                    >
                      {source.url}
                    </a>
                  </div>
                )}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Added {source.uploadDate}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        )}
      </div>
    </PageLayout>
  );
};

export default MemorySourcesPage;